import { useProject } from '@/contexts/ProjectContext';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { ArtifactStorageDisabledError, listArtifacts } from '@/lib/api';
import { ArtifactsListView } from '@/components/artifacts/ArtifactsListView';
import { CreateArtifactDialog } from '@/components/artifacts/CreateArtifactDialog';

export function ArtifactsPage(): React.ReactElement {
  const { selectedCodebase, isLoadingCodebases } = useProject();

  const storageProbe = useQuery({
    queryKey: ['artifacts-storage-probe', selectedCodebase?.id],
    queryFn: () =>
      selectedCodebase
        ? listArtifacts(selectedCodebase.id, { limit: 1 })
        : Promise.resolve({ artifacts: [] }),
    enabled: !!selectedCodebase,
    retry: false,
  });
  const storageDisabled = storageProbe.error instanceof ArtifactStorageDisabledError;

  if (isLoadingCodebases) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-text-secondary">
        Loading…
      </div>
    );
  }
  if (!selectedCodebase) {
    return (
      <div className="relative flex flex-1 flex-col items-center justify-center gap-3 px-4 py-12 text-center">
        <span
          aria-hidden
          className="fd-glow-flame pointer-events-none absolute inset-0 opacity-60"
        />
        <span className="relative flex size-12 items-center justify-center rounded-full border border-flame/30 bg-flame-bg-soft">
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-[radial-gradient(circle,oklch(0.72_0.17_55/0.3),transparent_70%)]"
          />
          <FileText className="relative size-5 text-flame-hi" />
        </span>
        <h1 className="relative text-lg font-semibold tracking-tight text-text-primary">
          Artifacts
        </h1>
        <p className="relative text-sm text-text-secondary">
          Select a codebase from the top nav to view its artifacts.
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <span aria-hidden className="fd-glow-flame pointer-events-none absolute inset-0 opacity-60" />
      <div className="relative flex items-start justify-between px-6 pt-5 pb-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-text-primary">Artifacts</h1>
          <p className="mt-1 text-xs text-text-tertiary">
            Documents and outputs produced by workflows · scoped to{' '}
            <span className="font-mono text-accent-bright">{selectedCodebase.name}</span>
          </p>
        </div>
        <CreateArtifactDialog codebaseId={selectedCodebase.id} storageDisabled={storageDisabled} />
      </div>
      <div className="relative flex-1 overflow-auto px-6 pb-4 pt-1">
        <ArtifactsListView codebase={selectedCodebase} />
      </div>
    </div>
  );
}
